import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FolderArchive, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { AttachmentList } from "./AttachmentList";
import { ordersApi } from "../api/orders";
import { useDeleteOrderAttachment, useUploadOrderAttachments } from "../hooks/useOrders";
import type { ManagedFileBrief } from "../types";

/**
 * Order attachments block. Read-only for everyone; when `canManage` is set
 * (same permission as the standard order edit — CEO), allows adding and removing files.
 */
export function OrderAttachmentsSection({
  orderId,
  files,
  canManage,
}: {
  orderId: string;
  files: ManagedFileBrief[];
  canManage: boolean;
}) {
  const [pending, setPending] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const uploadMutation = useUploadOrderAttachments();
  const deleteMutation = useDeleteOrderAttachment();
  const zipMutation = useMutation({
    mutationFn: () => ordersApi.downloadAttachmentsZip(orderId),
    onError: () => setError("Nie udało się pobrać załączników jako ZIP."),
  });

  // Preserve prior behaviour: hide the section entirely when there is nothing to show
  // and the user cannot add files.
  if (!canManage && !files.length) return null;

  const handleUpload = async () => {
    if (!pending.length) return;
    setError("");
    try {
      await uploadMutation.mutateAsync({ id: orderId, files: pending });
      setPending([]);
    } catch {
      setError("Nie udało się dodać załączników. Maksymalny rozmiar pliku to 25 MB.");
    }
  };

  const handleDelete = async (file: ManagedFileBrief) => {
    if (!window.confirm(`Usunąć załącznik „${file.original_filename}”?`)) return;
    setError("");
    setDeletingId(file.id);
    try {
      await deleteMutation.mutateAsync({ id: orderId, fileId: file.id });
    } catch {
      setError("Nie udało się usunąć załącznika.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Załączniki ({files.length})
        </p>
        {files.length > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] disabled:opacity-60 transition-colors"
            title="Pobierz wszystkie załączniki jako ZIP"
            disabled={zipMutation.isPending}
            onClick={() => {
              setError("");
              zipMutation.mutate();
            }}
          >
            {zipMutation.isPending ? <Spinner size="sm" /> : <FolderArchive size={14} />}
            ZIP
          </button>
        )}
      </div>

      {/* Jedno miejsce na błąd sekcji (upload / usunięcie / ZIP) — widoczne też dla
          użytkowników bez prawa edycji, którzy widzą tylko listę i przycisk ZIP. */}
      {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      {files.length > 0 ? (
        <AttachmentList
          files={files}
          onDelete={canManage ? handleDelete : undefined}
          deletingId={deletingId}
        />
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak załączników.</p>
      )}

      {canManage && (
        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          <FileDropZone files={pending} onFilesChange={setPending} multiple />
          {pending.length > 0 && (
            <div className="mt-2">
              <Button size="sm" loading={uploadMutation.isPending} onClick={handleUpload}>
                <Upload size={14} />{" "}
                {pending.length === 1
                  ? "Dodaj załącznik"
                  : `Dodaj załączniki (${pending.length})`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
